import React, { useState, useMemo, useRef } from "react";
import { View, StyleSheet, TouchableOpacity } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import ThemedInput from "@/components/ThemedInput";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { getTermFieldMetadata } from "@/types/schemas/terms";
import { TermPicker } from "@/components/Pickers/TermPicker";
import ContextMenu from "@/components/ContextMenu";
import { SkeletonRow } from "@/components/ui/Skeleton";
import useContextMenu from "@/hooks/useContextMenu";

export interface TermsFormProps {
  terms?: Record<string, any>;
  onUpdateTerms: (terms: Record<string, any>) => void;
  editing: boolean;
  loading?: boolean;
}

// Local components to avoid context dependencies
function TermsSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <View style={styles.section}>
      <ThemedText type="defaultSemiBold" style={styles.sectionTitle}>
        {title}
      </ThemedText>
      {children}
    </View>
  );
}

function TermInfoRow({ label, value }: { label: string; value: string }) {
  return (
    <View style={styles.infoRow}>
      <ThemedText style={{ opacity: 0.6 }}>{label}</ThemedText>
      <ThemedText>{value}</ThemedText>
    </View>
  );
}

interface EnumFieldProps {
  fieldKey: string;
  value: any;
  enumValues: string[];
  description: string;
  onUpdate: (value: string) => void;
  onRemove: () => void;
}

function EnumField({ fieldKey, value, enumValues, description, onUpdate, onRemove }: EnumFieldProps) {
  const { colors } = useTheme();
  const contextMenu = useContextMenu();
  const enumButtonRef = useRef<View>(null);

  const menuContent = (
    <View style={[styles.enumMenu, { backgroundColor: colors.background, borderColor: colors.border }]}>
      {enumValues.map((enumValue) => (
        <Button
          key={enumValue}
          text={enumValue}
          style={styles.enumMenuItem}
          onPress={() => {
            onUpdate(enumValue);
            contextMenu.setVisible(false);
          }}
        />
      ))}
    </View>
  );

  return (
    <View key={fieldKey} style={styles.termFieldItem}>
      <View style={styles.termFieldContent}>
        <ThemedText>{description}</ThemedText>
        <Button
          ref={enumButtonRef}
          text={value?.toString() || "Select..."}
          icon="chevron-down"
          style={{ gap: 8, backgroundColor: "transparent", paddingHorizontal: 6 }}
          iconPosition="right"
          onPress={() => {
            contextMenu.setIgnoreRef(enumButtonRef);
            enumButtonRef.current?.measureInWindow((pageX, pageY) =>
              contextMenu.setLocation({ x: pageX - 40, y: pageY + 30 }),
            );
            contextMenu.setMenuContent(menuContent);
            contextMenu.setVisible(true);
            contextMenu.setStyle({ backgroundColor: colors.background, overflow: "hidden" });
          }}
        />
      </View>
      <TouchableOpacity onPress={onRemove} style={styles.deleteButton}>
        <MaterialCommunityIcons name="close-circle" size={24} color={colors.dangerText} />
      </TouchableOpacity>
    </View>
  );
}

export function TermsForm({ terms = {}, onUpdateTerms, editing, loading = false }: TermsFormProps) {
  const { colors } = useTheme();
  const [showFieldPicker, setShowFieldPicker] = useState(false);

  // Get all field metadata
  const TERM_FIELDS = useMemo(() => getTermFieldMetadata(), []);

  // Get current field keys (only those with values)
  const currentFieldKeys = Object.keys(terms || {});

  const updateField = (fieldKey: string, value: any) => {
    onUpdateTerms({
      ...terms,
      [fieldKey]: value,
    });
  };

  const removeField = (fieldKey: string) => {
    const { [fieldKey]: removed, ...remainingTerms } = terms;
    onUpdateTerms(remainingTerms);
  };

  const formatValue = (fieldKey: string, value: any): string => {
    if (value === undefined || value === null) return "—";

    const fieldMeta = TERM_FIELDS[fieldKey];
    if (!fieldMeta) return String(value);

    // Boolean values
    if (fieldMeta.type === "boolean") {
      return value ? "Yes" : "No";
    }

    // Enum values - display as-is
    if (fieldMeta.type === "enum") {
      return String(value);
    }

    // Number values with special formatting
    if (fieldMeta.type === "number") {
      // Percentage fields
      if (fieldKey.includes("percentage") || fieldKey.includes("percent")) {
        return `${value}%`;
      }
      // Hour fields
      if (fieldKey.includes("hours") || fieldKey.includes("hour")) {
        return `${value} hours`;
      }
      // Multiplier fields
      if (fieldKey.includes("multiplier")) {
        return `${value}x`;
      }
      return String(value);
    }

    return String(value);
  };

  const renderField = (fieldKey: string) => {
    const fieldMeta = TERM_FIELDS[fieldKey];
    if (!fieldMeta) return null;

    const value = terms[fieldKey];
    const { label, description, type, enumValues } = fieldMeta;

    if (editing) {
      // Boolean field
      if (type === "boolean") {
        return (
          <View key={fieldKey} style={styles.termFieldItem}>
            <View style={styles.fieldRow}>
              <ThemedText>{description}</ThemedText>
              <TouchableOpacity
                onPress={() => updateField(fieldKey, !value)}
                style={[styles.toggle, { backgroundColor: value ? colors.primary : colors.border }]}
              >
                <View
                  style={[
                    styles.toggleThumb,
                    { backgroundColor: colors.background },
                    value && styles.toggleThumbActive,
                  ]}
                />
              </TouchableOpacity>
            </View>
            <TouchableOpacity onPress={() => removeField(fieldKey)} style={styles.deleteButton}>
              <MaterialCommunityIcons name="close-circle" size={24} color={colors.dangerText} />
            </TouchableOpacity>
          </View>
        );
      }

      // Enum field
      if (type === "enum" && enumValues && enumValues.length > 0) {
        return (
          <EnumField
            key={fieldKey}
            fieldKey={fieldKey}
            value={value}
            enumValues={enumValues}
            description={description}
            onUpdate={(newValue) => updateField(fieldKey, newValue)}
            onRemove={() => removeField(fieldKey)}
          />
        );
      }

      // Number or String field
      return (
        <View key={fieldKey} style={styles.termFieldItem}>
          <View style={styles.termFieldContent}>
            <ThemedInput
              label={description}
              value={value?.toString() || ""}
              onChangeText={(text) => {
                if (type === "number") {
                  const numValue = parseFloat(text);
                  updateField(fieldKey, isNaN(numValue) ? 0 : numValue);
                } else {
                  updateField(fieldKey, text);
                }
              }}
              keyboardType={type === "number" ? "numeric" : "default"}
            />
          </View>
          <TouchableOpacity onPress={() => removeField(fieldKey)} style={styles.deleteButton}>
            <MaterialCommunityIcons name="close-circle" size={24} color={colors.dangerText} />
          </TouchableOpacity>
        </View>
      );
    }

    // Preview mode
    return <TermInfoRow key={fieldKey} label={description || label} value={formatValue(fieldKey, value)} />;
  };

  if (loading) {
    return (
      <View style={styles.container}>
        <TermsSection title="">
          <SkeletonRow />
          <SkeletonRow />
          <SkeletonRow />
        </TermsSection>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <TermsSection title="">
        {currentFieldKeys.length > 0 ? (
          currentFieldKeys.map(renderField)
        ) : (
          <ThemedText style={styles.emptyText}>
            {editing ? "No terms configured. Click Add Field to begin." : "No terms configured"}
          </ThemedText>
        )}

        {editing && (
          <Button
            text={`+ Add Field (${Object.keys(TERM_FIELDS).length - currentFieldKeys.length} available)`}
            onPress={() => setShowFieldPicker(true)}
            style={styles.addButton}
          />
        )}
      </TermsSection>

      {/* Field Picker Modal */}
      <ContextMenu
        visible={showFieldPicker}
        setVisible={setShowFieldPicker}
        style={{
          top: "50%",
          left: "50%",
          transform: [{ translateX: -225 }, { translateY: -250 }],
          width: 450,
        }}
      >
        <TermPicker
          multiple={true}
          excludeFieldKeys={currentFieldKeys}
          onMultiPick={(fieldKeys) => {
            const newTerms = { ...terms };
            fieldKeys.forEach((fieldKey) => {
              const fieldMeta = TERM_FIELDS[fieldKey];
              let defaultValue: any;
              if (fieldMeta.type === "number") {
                defaultValue = 0;
              } else if (fieldMeta.type === "boolean") {
                defaultValue = false;
              } else if (fieldMeta.type === "enum" && fieldMeta.enumValues && fieldMeta.enumValues.length > 0) {
                defaultValue = fieldMeta.enumValues[0];
              } else {
                defaultValue = "";
              }
              newTerms[fieldKey] = defaultValue;
            });
            onUpdateTerms(newTerms);
            setShowFieldPicker(false);
          }}
          onCancel={() => setShowFieldPicker(false)}
        />
      </ContextMenu>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    height: "100%",
    padding: 16,
    gap: 16,
  },
  section: {
    gap: 8,
  },
  sectionTitle: {
    marginBottom: 4,
  },
  infoRow: {
    justifyContent: "space-between",
    gap: 4,
    paddingVertical: 4,
  },
  emptyText: {
    opacity: 0.5,
    textAlign: "center",
    paddingVertical: 20,
  },
  addButton: {
    marginTop: 8,
  },
  termFieldItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 8,
  },
  termFieldContent: {
    flex: 1,
    flexDirection: "row",
    gap: 8,
  },
  fieldRow: {
    flex: 1,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  deleteButton: {
    alignSelf: "flex-end",
    padding: 4,
  },
  toggle: {
    width: 48,
    height: 28,
    borderRadius: 14,
    padding: 2,
    justifyContent: "center",
  },
  toggleThumb: {
    width: 24,
    height: 24,
    borderRadius: 12,
  },
  toggleThumbActive: {
    alignSelf: "flex-end",
  },
  enumMenu: {
    borderWidth: 0,
  },
  enumMenuItem: {
    backgroundColor: "transparent",
    borderWidth: 0,
    justifyContent: "flex-start",
    gap: 8,
  },
});
